import type { AuthCallbackPath } from "shared-ui";

/**
 * Paths of the bankconnect JSON endpoints.
 *
 * Owning these paths here means they are greppable, they carry the canonical `/api` prefix in one
 * place, and they can be checked against the server: the shared contracts declare the same paths,
 * `urls.test.ts` asserts these match them, and the Python tests prove each contract URL routes to the
 * endpoint it names.
 */
const BASE = "/api/:customerSlug/bankconnect";

/** Path templates. Resolve with `getBankconnectUrls` rather than substituting by hand. */
export const BANKCONNECT_PATHS = {
  filterAccounts: `${BASE}/filter-accounts`,
  complete: `${BASE}/complete`,
  getSettings: `${BASE}/get-settings`,
  getStatementsData: `${BASE}/get-statements-data`,
  plaidConnected: `${BASE}/plaid/connected`,
  plaidReconnected: `${BASE}/plaid/reconnected`,
  plaidInstitutionLogo: `${BASE}/plaid/institution-logo`,
  plaidLinkToken: `${BASE}/plaid/link-token`,
  plaidLinkUpdateToken: `${BASE}/plaid/link-update-token`,
  mockedConnect: `${BASE}/mocked/connect`,
} as const;

export type BankconnectPathName = keyof typeof BANKCONNECT_PATHS;

export type BankconnectUrls = Record<BankconnectPathName, string>;

const resolve = (template: string, customerSlug: string): string =>
  template.replace(":customerSlug", encodeURIComponent(customerSlug));

/** Every bankconnect endpoint URL for one customer. */
export const getBankconnectUrls = (customerSlug: string): BankconnectUrls =>
  Object.fromEntries(
    Object.entries(BANKCONNECT_PATHS).map(([name, template]) => [name, resolve(template, customerSlug)])
  ) as BankconnectUrls;

// Deliberately not in `BANKCONNECT_PATHS`/`getBankconnectUrls`: the `?next_path=` this path carries is a
// per-caller choice of where to return after Basiq consent, not just an address. Listing the bare path
// there would let a caller resolve it and silently drop that callback, so `basiqStartAuthFlowUrl` is the
// only way to build it.
const BASIQ_START_AUTH_FLOW = `${BASE}/basiq/start-auth-flow`;

/** The basiq start-auth-flow URL, carrying the caller's choice of where to return after Basiq consent. */
export const basiqStartAuthFlowUrl = (customerSlug: string, nextPath: AuthCallbackPath): string =>
  `${resolve(BASIQ_START_AUTH_FLOW, customerSlug)}?${new URLSearchParams({
    next_path: nextPath,
  }).toString()}`;
